import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowUp, FaArrowDown, FaXmark } from "react-icons/fa6";
import Button from "@components/forms/Button/Button";
import AddItemListModal from "@components/modals/AddItemListModal/AddItemListModal";
import './List.scss';

type ListItemType = {
    label: string,
    description: string,
    value: string,
    disabled: boolean,
};

type ListProps = {
    className?: string,
    label: string,
    values: Array<string>,
    items: Array<ListItemType>,
    iconPath: string,
    showDisabledItems?: boolean,
    onChange: (values: Array<string>) => void,
};

function List(props: ListProps) {
    const [forceUpdate, setForceUpdate] = useState<number>(0);
    const [values, setValues] = useState<Array<ListItemType>>([]);
    const [modalVisible, setModalVisible] = useState<boolean>(false);

    const { t } = useTranslation();

    const itemsNotSelected: Array<ListItemType> = useMemo(() => {
        return props.items.filter((i: ListItemType) => !props.values.includes(i.value) && (props.showDisabledItems || !i.disabled));
    }, [props.values, props.items, props.showDisabledItems]);

    const handleChangeOrder = (item: ListItemType, isUp: boolean) => {
        const currentIndex = values.findIndex((i: ListItemType) => i.value === item.value);
        if(currentIndex === -1)
            return;

        const newPosition = Math.min(Math.max(0, currentIndex+(isUp ? -1 : 1)), values.length-1);

        const updatedValues = values;
        updatedValues.splice(currentIndex, 1);
        updatedValues.splice(newPosition, 0, item);

        setValues(updatedValues);
        props.onChange(updatedValues.map((i: ListItemType) => i.value));
        setForceUpdate(prev => prev+1);
    };

    const handleDelete = (item: ListItemType) => {
        const updatedValues = values.filter((i: ListItemType) => i.value !== item.value);
        setValues(updatedValues);
        props.onChange(updatedValues.map((i: ListItemType) => i.value));
    };

    const handleAdd = (item: ListItemType) => {
        if(values.find((i: ListItemType) => i.value === item.value))
            return;
        const updatedValues = [...values, item];
        setValues(updatedValues);
        props.onChange(updatedValues.map((i: ListItemType) => i.value));
    };

    useEffect(() => {
        setValues(props.items.filter((i: ListItemType) => props.values.includes(i.value)).sort((a, b) => {
            return props.values.indexOf(a.value) - props.values.indexOf(b.value);
        }));
    }, [props.values]);

    return (
        <div className={`list ${props.className ?? ''}`}>
            <span className="list-label">{props.label}</span>
            <ul className="input-list">
                {values.map((item: ListItemType, index: number) => (
                    <li key={index} className={item.disabled ? 'item-disabled' : ''}>

                        <span className="icon" style={{
                            backgroundImage: `url("${props.iconPath}${item.value.toLocaleLowerCase()}.png")`,
                        }}></span>
                        <span className="symbol">{item.value}</span>
                        <span className="description">{item.description}</span>
                        {item.disabled && (
                            <span className="disabled">Disabled</span>
                        )}
                        <div className="actions">
                            {index > 0 && (
                                <span onClick={() => handleChangeOrder(item, true)}><FaArrowUp size="1.1rem"/></span>
                            )}
                            {values && index < values.length-1 && (
                                <span onClick={() => handleChangeOrder(item, false)}><FaArrowDown size="1.1rem"/></span>
                            )}
                            <span onClick={() => handleDelete(item)}><FaXmark size="1.1rem"/></span>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="row g-1 align-items-center">
                <Button size="sm" filled onClick={() => setModalVisible(true)}>{t('text.add')}</Button>
            </div>

            <AddItemListModal visible={modalVisible} items={itemsNotSelected} iconPath={props.iconPath} onHide={() => setModalVisible(false)} onCreate={handleAdd}/>
        </div>
    );
}

export type {
    ListItemType,
};
export default List;
